import { observer } from "mobx-react-lite";
import { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { Button } from "antd";
import { IProjectModel } from "../../stores/project/projectModel";
import { MSTContext } from "../../stores/Root";
import { FontInput, AccountFontInput } from "../../API";
import { font2Hash } from "../../utils/font";
import { getSnapshot, isStateTreeNode } from "mobx-state-tree";
import { AsyncOpState } from "../../types/enums/async-op-states";
import { LayersAndModsPerScene } from "../../types/EditVideoTypes";
import SdkPlayer, { SdkPlayerProps } from "../../components/SdkPlayer";
import {
  IVideoPart,
  IVideoPartFilled,
} from "@blings/blings-player/lib/src/fetchData/getVideoParts";
import "./EditVideo.scss";
import EditVideoMenu from "./EditVideoMenu";
import EditView from "./EditView";
import { BlingsBtn } from "../../components/antd-extensions/blings-btn.component";
import { getENV } from "../../config";
import { IExperiment } from "@blings/blings-player/lib/src/SDK/sdk.api";
import { ExperimentValues } from "@blings/blings-player/lib/src/SDK/sdk.api";
import GlobalMuteButton from "../../components/GlobalMuteButton";
import { IJSONObject } from "@blings/blings-player/lib/src/typings/basic";
import { ExtractedValues } from "../../utils/getTypesFromLiveControl";
import { UseCaseTemplateEditor } from "../WelcomeProjectsGallery/UseCaseGallery/UseCaseTemplateEditor";
import {
  getTypesFromLiveControlSchema,
  extractFontsFromAccount,
} from "../WelcomeProjectsGallery/UseCaseGallery/helpers";

function safeGetSnapshot<T>(node: T): T {
  return isStateTreeNode(node) ? getSnapshot(node) : node;
}

function isOverlayAudioDifferent(overlayOne: any, overlayTwo: any) {
  const overlayOneAudio =
    overlayOne?.audio?.[0] && overlayOne.audio?.[0]?.url
      ? overlayOne.audio?.[0]
      : undefined;
  const overlayTwoAudio =
    overlayTwo?.audio?.[0] && overlayTwo.audio?.[0]?.url
      ? overlayTwo.audio?.[0]
      : undefined;

  if (!overlayOneAudio && !overlayTwoAudio) {
    return false;
  } else {
    const one = JSON.stringify(safeGetSnapshot(overlayOne ?? {}));
    const two = JSON.stringify(safeGetSnapshot(overlayTwo ?? {}));

    return one !== two;
  }
}

// the order of the fonts in the array is not important
export function isFontsAreDifferent(
  fontsOne: FontInput[],
  fontsTwo: FontInput[]
): boolean {
  if (fontsOne.length !== fontsTwo.length) {
    return true;
  }
  const mapFonts1 = fontsOne.reduce<Record<string, string>>((acc, font) => {
    acc[font2Hash(font)] = font.url;
    return acc;
  }, {});
  const mapFonts2 = fontsTwo.reduce<Record<string, string>>((acc, font) => {
    acc[font2Hash(font)] = font.url;
    return acc;
  }, {});
  const keys1 = Object.keys(mapFonts1);

  for (let key of keys1) {
    if (mapFonts2[key] === mapFonts1[key]) {
      delete mapFonts2[key];
      delete mapFonts1[key];
      continue;
    } else {
      return true;
    }
  }
  if (Object.keys(mapFonts2).length || Object.keys(mapFonts1).length) {
    return true;
  }
  return false;
}

const DEFAULT_MENU_ITEMS = ["Text", "Interactives", "Media & Controls"];
const LEVEL_0 = "level_0";

const EditVideo = observer(() => {
  const store = useContext(MSTContext);
  const { selectedProject, projectLoadingStatus } = store.projectsStore;
  const project = selectedProject as IProjectModel;
  const { setSearchView, setCurrentSearch } = store.editVideoStore;
  const { accountFromId } = store.userStore;
  const hasUnpublishedConnnectorChanges =
    project?.hasUnpublishedConnnectorChanges;

  const account = accountFromId(project.account?.id ?? "");

  const [allLayersAndMods, setAllLayersAndMods] = useState<
    LayersAndModsPerScene[]
  >([]);
  const [publishButtonState, setPublishButtonState] = useState<AsyncOpState>(
    AsyncOpState.Changed
  );
  const [activePanels, setActivePanels] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isPlayerLoading, setIsPlayerLoading] = useState<boolean>(false);
  const [selectedScene, setSelectedScene] = useState<string>("");
  const [accountFonts, setAccountFonts] = useState<AccountFontInput[]>([]);
  const [isFetchingAccountFonts, setIsFetchingAccountFonts] = useState(true);
  const [isUploadingFont, setIsUploadingFont] = useState(false);

  const publishButtonTexts = {
    [AsyncOpState.Changed]: "Publish Changes",
    [AsyncOpState.Saving]: "Publishing",
    [AsyncOpState.Success]: "Published",
    [AsyncOpState.Error]: "Error",
  };

  const [menuItems, setMenuItems] = useState<string[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>("");

  if (!project) {
    return null;
  }

  // Compute branding changes directly from the store values.
  const unsavedBrandingChanges =
    JSON.stringify(safeGetSnapshot(project.settingsDraftInEdit)) !==
      JSON.stringify(safeGetSnapshot(project.settingsDraft)) ||
    isOverlayAudioDifferent(project.overlayDraftInEdit, project.overlayDraft) ||
    isFontsAreDifferent(
      project.fontOverridesDraftInEdit || [],
      project.fontOverridesDraft || []
    );

  const unpublishedBrandingChanges =
    JSON.stringify(safeGetSnapshot(project.settingsDraft)) !==
      JSON.stringify(safeGetSnapshot(project.settings)) ||
    isOverlayAudioDifferent(project.overlay, project.overlayDraft) ||
    isFontsAreDifferent(
      project.fontOverridesDraft || [],
      project.fontOverrides || []
    );

  const extractGroups = (settingsSchema: IJSONObject) => {
    const schemeObject = JSON.parse(JSON.stringify(settingsSchema));
    const nestedGroups: IJSONObject[] = [];
    const level1Elements: Record<string, IJSONObject> = {};

    const groups = Object.keys(schemeObject?.properties || {});

    groups.forEach((group) => {
      if (schemeObject["properties"][group]?.["properties"]) {
        level1Elements[group] = schemeObject["properties"][group][
          "properties"
        ] as IJSONObject;
      } else {
        if (!level1Elements[LEVEL_0]) {
          level1Elements[LEVEL_0] = {};
        }
        (level1Elements[LEVEL_0] as IJSONObject)[group] = schemeObject[
          "properties"
        ][group] as IJSONObject;
      }
    });

    nestedGroups.push({ level_0: level1Elements });

    return level1Elements;
  };

  const destructureGroups = useMemo<Record<string, any>>(() => {
    if (project.settingsSchemaDraft?.properties) {
      const groups: IJSONObject = {};
      Object.keys(project.settingsSchemaDraft?.properties).forEach((key) => {
        groups[key] = JSON.parse(
          JSON.stringify(extractGroups(project.settingsSchemaDraft?.properties[key]))
        );
      });
      return groups;
    }
    return {};
  }, [project.settingsSchemaDraft?.properties]);

  const lcsbk = useMemo(() => {
    if (project.settingsSchemaDraft?.properties) {
      const groups = destructureGroups;
      const lcsbk: { [key: string]: { [key: string]: ExtractedValues } } = {};
      for (const key in groups) {
        if (typeof groups[key] === "object") {
          for (const subkey in groups[key] as IJSONObject) {
            const temp = getTypesFromLiveControlSchema(
              (groups[key] as IJSONObject)[subkey] as IJSONObject
            );
            if (!lcsbk[key]) {
              lcsbk[key] = {};
            }
            lcsbk[key][subkey] = temp;
          }
        }
      }

      return lcsbk;
    }
    return {};
  }, [project.settingsSchemaDraft?.properties, destructureGroups]);

  // Update live control directly in the store.
  const handleLiveControlChange = useCallback(
    (
      keyAndValue: IJSONObject,
      subGroup: string = "",
      selectedMenuItem: string
    ) => {
      const newLiveControl = JSON.parse(
        JSON.stringify(project.settingsDraftInEdit)
      );
      if (subGroup !== "" && subGroup !== LEVEL_0) {
        newLiveControl[selectedMenuItem][subGroup] = {
          ...newLiveControl[selectedMenuItem][subGroup],
          ...keyAndValue,
        };
      } else {
        newLiveControl[selectedMenuItem] = {
          ...newLiveControl[selectedMenuItem],
          ...keyAndValue,
        };
      }
      const newSettings = {
        ...project.settingsDraftInEdit,
        ...newLiveControl,
      };

      project.updateDraftLiveControlDataInEdit(newSettings);
    },
    [project]
  );

  const handleOverlayAudioChange = useCallback(
    (
      newOverlayAudio:
        | { url: string; loop: boolean; volume: number }
        | ((prevState: { url: string; loop: boolean; volume: number }) => {
            url: string;
            loop: boolean;
            volume: number;
          })
    ) => {
      const audioState =
        typeof newOverlayAudio === "function"
          ? newOverlayAudio({
              url: project.overlayDraftInEdit?.audio?.[0]?.url || "",
              loop: project.overlayDraftInEdit?.audio?.[0]?.loop || true,
              volume: project.overlayDraftInEdit?.audio?.[0]?.volume || 1,
            })
          : newOverlayAudio;

      if (project.overlayDraftInEdit && project.overlayDraftInEdit.audio) {
        project.updateOverlayDraftInEdit({ audio: [audioState] });
      }
    },
    [project, project.overlayDraftInEdit]
  );

  const overlayAudio = useMemo(() => {
    // Create a new object to ensure React detects the change
    const result = {
      url: project.overlayDraftInEdit?.audio?.[0]?.url ?? "",
      loop: project.overlayDraftInEdit?.audio?.[0]?.loop ?? true,
      volume: project.overlayDraftInEdit?.audio?.[0]?.volume ?? 1,
    };
    if (!result.url) {
      return null;
    }
    return result;
  }, [
    project.overlayDraftInEdit,
    project.overlayDraftInEdit?.audio?.[0]?.url,
    project.overlayDraftInEdit?.audio?.[0]?.loop,
    project.overlayDraftInEdit?.audio?.[0]?.volume,
  ]);

  const handleSaveBrandingDraft = async () => {
    setIsSaving(true);
    try {
      await project.saveBrandingDraft();
    } catch (e) {
      console.error(e);
    }
    setIsSaving(false);
  };

  const handlePanelCollapse = (keys: string[] | string) => {
    if (!keys) setActivePanels([]);
    else {
      typeof keys === "string"
        ? activePanels.includes(keys)
          ? setActivePanels([])
          : setActivePanels([keys])
        : setActivePanels(keys);
    }
  };

  const handlePublishChanges = async () => {
    try {
      setIsSaving(true);
      setPublishButtonState(AsyncOpState.Saving);
      await project.publishProject();
      setPublishButtonState(AsyncOpState.Changed);
    } catch (e) {
      console.error(e);
      setPublishButtonState(AsyncOpState.Error);
    }
    // Reset after 3 seconds
    setTimeout(() => {
      setPublishButtonState(AsyncOpState.Changed);
      setIsSaving(false);
    }, 3000);
  };

  const handleMenuChange = (menuItem: string) => {
    if (
      !DEFAULT_MENU_ITEMS.includes(selectedMenuItem) ||
      !DEFAULT_MENU_ITEMS.includes(menuItem)
    ) {
      setSelectedScene("");
    }
    setActivePanels([]);
    setSelectedMenuItem(menuItem);
  };

  const handleOverlayAudioChangeWithSave = useCallback(
    (
      newOverlayAudio:
        | { url: string; loop: boolean; volume: number }
        | ((prevState: { url: string; loop: boolean; volume: number }) => {
            url: string;
            loop: boolean;
            volume: number;
          })
    ) => {
      handleOverlayAudioChange(newOverlayAudio);
      project.saveOverlayDraft();
    },
    [handleOverlayAudioChange, project]
  );

  useEffect(() => {
    if (
      project.instantiatedFromUseCase &&
      projectLoadingStatus === AsyncOpState.Success &&
      menuItems.length === 0
    ) {
      const allItems = [
        ...Object.keys(project.settingsSchemaDraft?.properties || {}),
        ...DEFAULT_MENU_ITEMS,
      ];
      setMenuItems(allItems);
      setSelectedMenuItem(allItems[0]);
    } else if (!project.instantiatedFromUseCase && menuItems.length === 0) {
      setMenuItems(DEFAULT_MENU_ITEMS);
      setSelectedMenuItem(DEFAULT_MENU_ITEMS[0]);
    }
  }, [
    menuItems,
    project.instantiatedFromUseCase,
    projectLoadingStatus,
    project.settingsDraftInEdit,
    project.settingsSchemaDraft?.properties,
  ]);

  const hasVideoParts = !!project.workspaceVideoParts.length;
  if (!hasVideoParts)
    return (
      <div
        className="edit-content-main"
        style={{ alignItems: "center", marginTop: "30vh" }}
      >
        Create a video using Blings extension, and see a demo here
      </div>
    );

  const uniqueFontsInVideoParts = useMemo(() => {
    return Object.values(
      project.workspaceVideoParts.reduce(
        (acc: Record<string, FontInput>, part) => {
          const videoPart = safeGetSnapshot(part) as IVideoPart;
          if (videoPart.fonts) {
            videoPart.fonts.forEach((font: FontInput) => {
              acc[font2Hash(font)] = font;
            });
          }
          return acc;
        },
        {} as Record<string, FontInput>
      )
    ) as FontInput[];
  }, [project.workspaceVideoParts]);

  if (projectLoadingStatus === AsyncOpState.Saving) {
    return <div>Loading...</div>;
  } else if (projectLoadingStatus === AsyncOpState.Error) {
    return <div>Error</div>;
  }

  const handleBackToFullVideo = useCallback(() => {
    const lineupId = project.flowDiagramSnapshot?.nodes?.[0]?.lineupId;
    if (lineupId) {
      setSelectedScene(`{ lineup: "${lineupId}" }`);
      setActivePanels([]);
    } else {
      setSelectedScene(project.videoPartNames[0]);
      setActivePanels([]);
    }
  }, [project.flowDiagramSnapshot]);

  useEffect(() => {
    if (account) {
      const fetchFonts = async () => {
        // setIsFetchingAccountFonts(true);
        try {
          await account.refreshFonts();
          const fonts = extractFontsFromAccount(account.fonts || []);
          setAccountFonts(fonts);
        } catch (error) {
          console.error("Failed to fetch account fonts:", error);
          // Optionally set an error state here
        } finally {
          setIsFetchingAccountFonts(false);
        }
      };

      // Initial fetch + if account changes
      fetchFonts();
    }
  }, [account]);

  const fontsOverrides = useMemo(() => {
    return project.fontOverridesDraftInEdit || [];
  }, [project.fontOverridesDraftInEdit]);

  const updatedScenes = useMemo(() => {
    const scenes: IVideoPartFilled[] = [];
    project.workspaceVideoParts?.forEach((videoPart: any) => {
      scenes.push({
        name: videoPart.name,
        json: videoPart.jsonData,
        modsArr:
          videoPart.modsArr?.map((mod: any) => {
            return JSON.parse(mod.dataStr);
          }) || [],
      });
    });
    return scenes;
  }, [project.workspaceVideoParts, allLayersAndMods]);

  const sdkParams = useMemo(() => {
    if (!project) return null;
    return {
      project: {
        projectId: project.id,
        env: getENV(),
        liveControlData: project.settingsDraftInEdit,
        videoParts: updatedScenes,
      },
      experiments: JSON.parse(
        JSON.stringify(project.experiments?.changedDraftExperiments)
      ) as IExperiment<ExperimentValues>[],
      variantSelectionSet: project?.experiments?.selectedVariantSet,
      data: {},
      scenes: [
        selectedScene ||
          (project.flowDiagramDraft?.lineups[0] && {
            lineup: project.flowDiagramDraft?.lineups[0],
          }) ||
          project.workspaceVideoParts[0]?.name,
      ],
      flowDiagram: project.flowDiagramSnapshot ?? undefined,
      settings: {
        hideCover: true,
        autoplay: false,
        fontOverrides: fontsOverrides ?? undefined,
        ...(overlayAudio ? { overlay: { audio: [overlayAudio] } } : {}),
      },
    } as SdkPlayerProps["sdkParams"];
  }, [
    project,
    selectedScene,
    updatedScenes,
    project.settingsDraftInEdit,
    fontsOverrides,
    overlayAudio,
  ]);

  return (
    <div className="edit-content-main">
      <div className="Container">
        <div className="menu-wrapper">
          <EditVideoMenu
            menuItems={menuItems}
            selectedMenuItem={selectedMenuItem}
            setSelectedMenuItem={handleMenuChange}
          />
        </div>
        <div className="elements-wrapper">
          {DEFAULT_MENU_ITEMS.includes(selectedMenuItem) ? (
            <div className="edit-default-elements-wrapper">
              <EditView
                project={project}
                overlayAudio={overlayAudio}
                handleOverlayAudioChange={handleOverlayAudioChangeWithSave}
                selectedScene={selectedScene}
                handlePanelCollapse={handlePanelCollapse}
                activePanels={activePanels}
                setAllLayersAndMods={setAllLayersAndMods}
                allLayersAndMods={allLayersAndMods}
                setCurrentSearch={setCurrentSearch}
                setSearchView={setSearchView}
                hasUnpublishedBrandingChanges={unsavedBrandingChanges}
                selectedMenuItem={selectedMenuItem}
                setSelectedMenuItem={setSelectedMenuItem}
                setSelectedScene={setSelectedScene}
              />
            </div>
          ) : (
            <div className="edit-use-case-wrapper">
              {selectedMenuItem && (
                <UseCaseTemplateEditor
                  overlayAudio={overlayAudio}
                  handleOverlayAudioChange={handleOverlayAudioChange}
                  project={project}
                  account={account}
                  setIsSavingTemplate={setIsSaving}
                  liveControl={project.settingsDraftInEdit}
                  handleLiveControlChange={handleLiveControlChange}
                  accountFonts={accountFonts}
                  onFontUploadComplete={setAccountFonts}
                  isUploadingFont={isUploadingFont}
                  setIsUploadingFont={setIsUploadingFont}
                  fontsInVideoParts={uniqueFontsInVideoParts}
                  fontOverrides={fontsOverrides || []}
                  setFontOverrides={project.setFontOverridesDraftInEdit}
                  useCaseName={selectedMenuItem}
                  lcsbk={lcsbk || {}}
                  destructureGroups={destructureGroups}
                />
              )}
            </div>
          )}
        </div>
        <div className="edit-content-publish-buttons">
          <div className="button-wrapper">
            <div>
              {(hasUnpublishedConnnectorChanges ||
                unpublishedBrandingChanges) &&
                "Done editing?"}
            </div>
            <div id="PublishButton" className="publish-button">
              <div className="publish-buttons-wrapper">
                {!DEFAULT_MENU_ITEMS.includes(selectedMenuItem) && (
                  <div className="save-draft-btn-wrapper">
                    <Button
                      className="saving-draft-button"
                      disabled={!unsavedBrandingChanges || isSaving}
                      onClick={handleSaveBrandingDraft}
                    >
                      Save
                    </Button>
                  </div>
                )}
                <BlingsBtn
                  className={"publish ButtonEditVideo"}
                  opState={publishButtonState}
                  htmlType={"submit"}
                  btnTexts={publishButtonTexts}
                  onClick={handlePublishChanges}
                  disabled={
                    !(
                      hasUnpublishedConnnectorChanges ||
                      unpublishedBrandingChanges
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="edit-video-player-wrapper">
        <div
          className="player-wrapper-inner"
          style={{
            height: isPlayerLoading ? "100%" : "fit-content",
          }}
        >
          <SdkPlayer
            sdkParams={sdkParams}
            outsideLoading={setIsPlayerLoading}
            isFetchingData={isFetchingAccountFonts}
          />

          <div className="command-mute-btn-wrapper">
            <div className="mute-btn-wrapper">
              <GlobalMuteButton />
            </div>
          </div>
          <div
            className="back-to-full-video-btn"
            style={{ display: selectedScene ? "inline-flex" : "none" }}
          >
            <span>Previewing a Scene</span>
            <Button onClick={handleBackToFullVideo}>Back to full video</Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default EditVideo;
